/**
 * Layout-related type definitions
 *
 * Centralized types used across layout components (AppShell, Sidebar, Header, etc.).
 * These types define the responsive breakpoint system, navigation structure, and
 * shell component interfaces.
 *
 * @module types/layout
 */

import type { SidebarBehavior } from '@/hooks';
import type {
  HeaderVariant,
  HeaderContextMenuItem,
} from '@/layouts/components/Header';
import type { ReactNode } from 'react';

// =============================================================================
// BREAKPOINT TYPES
// =============================================================================

/**
 * Size-based breakpoint keys aligned with Tailwind CSS.
 *
 * - `sm`: 640px (small devices)
 * - `md`: 768px (medium devices)
 * - `lg`: 1024px (large devices)
 * - `xl`: 1280px (extra large devices)
 */
export type BreakpointKey = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Breakpoint pixel threshold values aligned with Tailwind CSS.
 *
 * Use with `useBreakpoint` hook functions:
 * - `isAbove(bp)`: width >= BREAKPOINTS[bp]
 * - `isBelow(bp)`: width < BREAKPOINTS[bp]
 * - `isAt(bp)`: within the breakpoint range
 * - `isBetween(min, max)`: width >= min && width < max
 *
 * @example
 * ```tsx
 * const { isAbove, isBelow } = useBreakpoint();
 *
 * // Show sidebar on md and above (>= 768px)
 * if (isAbove('md')) {
 *   return <Sidebar />;
 * }
 *
 * // Show mobile nav below md (< 768px)
 * if (isBelow('md')) {
 *   return <MobileNav />;
 * }
 * ```
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
} as const;

// =============================================================================
// SIDEBAR TYPES
// =============================================================================

// =============================================================================
// NAVIGATION TYPES
// =============================================================================

/**
 * Navigation item definition for sidebar, tabs, and mobile drawer.
 *
 * @example
 * ```tsx
 * const navItem: NavItem = {
 *   id: 'dashboard',
 *   label: 'Dashboard',
 *   icon: <HomeIcon />,
 *   href: '/dashboard',
 *   badge: 3, // notification count
 * };
 * ```
 */
export interface NavItem {
  /** Unique identifier for the navigation item */
  id: string;
  /** Display label shown to users */
  label: string;
  /** Icon element rendered alongside the label */
  icon: ReactNode;
  /** Navigation target URL */
  href: string;
  /** Optional badge content (notification count or status) */
  badge?: string | number | undefined;
  /** Nested navigation items for expandable menus */
  children?: NavItem[];
}

// =============================================================================
// VIEW MODE TYPES
// =============================================================================

/**
 * Dashboard view mode variants.
 *
 * Controls which modules and layout are displayed on the dashboard:
 * - `default`: Standard client dashboard with personal stats
 * - `social`: Community-focused view with friend activity and leaderboards
 * - `analytics`: Data-heavy view with charts and detailed metrics
 * - `trainer`: Professional view with client management features
 *
 * @example
 * ```tsx
 * const [viewMode, setViewMode] = useState<ViewMode>('default');
 *
 * const handleModeChange = (mode: ViewMode) => {
 *   setViewMode(mode);
 *   analytics.track('dashboard_view_changed', { mode });
 * };
 * ```
 */
export type ViewMode = 'default' | 'social' | 'analytics' | 'trainer';

// =============================================================================
// HEADER CONFIG TYPES
// =============================================================================

/**
 * Live configuration a page can push into the shell `Header` so a form page can
 * take over the global header (✕ close · title · Save) instead of rendering its
 * own in-content header.
 *
 * Set/cleared via the `useHeaderConfig` hook and surfaced through
 * {@link AppShellContextValue}. Kept live so the primary action's
 * `disabled`/`loading` reflect real form state.
 *
 * @example
 * ```tsx
 * useHeaderConfig(
 *   () => ({
 *     variant: 'action',
 *     title: isEditing ? 'Edit Workout' : 'New Workout',
 *     primaryAction: { label: 'Save', onClick: handleSave, disabled: !name, loading: isSaving },
 *     onClose: handleBack,
 *     hideTabBar: true,
 *   }),
 *   [isEditing, name, isSaving, handleSave, handleBack]
 * );
 * ```
 */
export interface HeaderConfig {
  /** Header variant to render — `'action'` for full-screen form editors */
  variant: HeaderVariant;
  /** Title shown centered in the header (subpage/action variants) */
  title?: string;
  /** Primary commit action (action variant) — e.g. Save */
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
  /** Called when the close (✕) button is clicked — leaves the editor */
  onClose?: () => void;
  /** Hide the bottom tab bar for a true full-screen editor (default true when `variant === 'action'`) */
  hideTabBar?: boolean;
  /** Back handler for the subpage variant's ← button. */
  onBack?: () => void;
  /** Overflow (⋯) menu items for the subpage variant. */
  contextMenuItems?: HeaderContextMenuItem[];
}

// =============================================================================
// APP SHELL INTERFACES
// =============================================================================

/**
 * Context value for the AppShell layout provider.
 *
 * Provides layout state and controls to all child components.
 * Access via the `useAppShell` hook.
 *
 * @example
 * ```tsx
 * const { sidebarExpanded, toggleSidebar, sidebarBehavior } = useAppShell();
 *
 * if (sidebarBehavior === 'hidden') {
 *   return <MobileNav />;
 * }
 * return <DesktopSidebar expanded={sidebarExpanded} onToggle={toggleSidebar} />;
 * ```
 */
export interface AppShellContextValue {
  /** Sidebar behavior mode based on content-width rules */
  sidebarBehavior: SidebarBehavior;
  /** Whether sidebar is expanded (tablet/desktop only) */
  sidebarExpanded: boolean;
  /** Toggle sidebar expanded state */
  toggleSidebar: () => void;
  /** Set sidebar expanded state */
  setSidebarExpanded: (expanded: boolean) => void;
  /** Whether mobile drawer is open */
  mobileDrawerOpen: boolean;
  /** Toggle mobile drawer */
  toggleMobileDrawer: () => void;
  /** Set mobile drawer open state */
  setMobileDrawerOpen: (open: boolean) => void;
  /** Current dashboard view mode for display (null when not on dashboard) */
  dashboardViewMode: ViewMode | null;
  /** Update displayed view mode (called by DashboardPage when profile changes) */
  setDashboardViewMode: (mode: ViewMode | null) => void;
  /** Callback for user-initiated view mode changes (registered by DashboardPage) */
  onDashboardViewModeChange: ((mode: ViewMode) => void) | null;
  /** Register/unregister the view mode change handler */
  setOnDashboardViewModeChange: (
    handler: ((mode: ViewMode) => void) | null
  ) => void;
  /** Active header configuration pushed by a page (null = default `main` header) */
  headerConfig: HeaderConfig | null;
  /** Set or clear the header configuration (use the `useHeaderConfig` hook) */
  setHeaderConfig: (config: HeaderConfig | null) => void;
}

// =============================================================================
// LAYOUT CONSTANTS
// =============================================================================

/**
 * Layout dimension constants in pixels.
 *
 * Use these values for consistent spacing and sizing across layout components.
 * All values are in pixels unless otherwise noted.
 *
 * @example
 * ```tsx
 * import { LAYOUT_DIMENSIONS } from '@/types/layout';
 *
 * const MainContent = styled.main`
 *   padding-top: ${LAYOUT_DIMENSIONS.headerHeightDesktop}px;
 *   margin-left: ${sidebarExpanded
 *     ? LAYOUT_DIMENSIONS.sidebarWidthExpanded
 *     : LAYOUT_DIMENSIONS.sidebarWidthCollapsed}px;
 * `;
 * ```
 */
export const LAYOUT_DIMENSIONS = {
  /** Header height on mobile */
  headerHeightMobile: 56,
  /** Header height on tablet/desktop */
  headerHeightDesktop: 64,
  /** Bottom tab bar height (excluding safe area) */
  tabBarHeight: 64,
  /** Sidebar width when expanded */
  sidebarWidthExpanded: 240,
  /** Sidebar width when collapsed */
  sidebarWidthCollapsed: 72,
  /** Mobile drawer width */
  mobileDrawerWidth: 280,
  /** Mobile drawer max width percentage */
  mobileDrawerMaxWidthPercent: 80,
} as const;
